import * as L from "leaflet";
import * as turf from "@turf/turf";

type EdgeMeasuredLayer = L.Polyline & {
  _edgeMeasureLabels?: L.Marker[];
  _edgeMeasureCleanup?: () => void;
};

const EDGE_LABEL_ICON_CLASS = "bg-transparent border-0 pointer-events-none";
const EDGE_LABEL_SPAN_CLASS =
  "inline-block -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded border border-[rgba(12,40,86,0.28)] bg-[rgba(255,255,255,0.94)] px-[5px] py-0.5 text-[11px] font-semibold leading-[1.2] text-[#0C2856] shadow-[0_1px_3px_rgba(0,0,0,0.18)]";

const getPrimaryLatLngs = (layer: L.Polyline): L.LatLng[] => {
  const rawLatLngs = layer.getLatLngs();
  if (!Array.isArray(rawLatLngs)) return [];

  if ((rawLatLngs[0] as L.LatLng | undefined)?.lat !== undefined) {
    return rawLatLngs as L.LatLng[];
  }

  if (Array.isArray(rawLatLngs[0])) {
    return rawLatLngs[0] as L.LatLng[];
  }

  return [];
};

const formatDistance = (distanceInKm: number): string => {
  if (distanceInKm < 1) return `${Math.round(distanceInKm * 1000)} m`;
  return `${distanceInKm.toFixed(1)} km`;
};

const getMidpoint = (start: L.LatLng, end: L.LatLng): L.LatLng => {
  const midpoint = turf.midpoint(
    turf.point([start.lng, start.lat]),
    turf.point([end.lng, end.lat])
  );
  const [lng, lat] = midpoint.geometry.coordinates;
  return L.latLng(lat, lng);
};

const getSegmentDistance = (start: L.LatLng, end: L.LatLng): string => {
  const distanceInKm = turf.distance(
    turf.point([start.lng, start.lat]),
    turf.point([end.lng, end.lat]),
    { units: "kilometers" }
  );
  return formatDistance(distanceInKm);
};

const removeEdgeMeasureLabels = (layer: EdgeMeasuredLayer) => {
  layer._edgeMeasureLabels?.forEach((label) => label.remove());
  layer._edgeMeasureLabels = [];
};

export const uninstallEdgeMeasureLabels = (layer: L.Polyline) => {
  const measuredLayer = layer as EdgeMeasuredLayer;
  removeEdgeMeasureLabels(measuredLayer);
  measuredLayer._edgeMeasureCleanup?.();
  measuredLayer._edgeMeasureCleanup = undefined;
};

export const updateEdgeMeasureLabels = (
  layer: L.Polyline,
  map: L.Map,
  closeShape: boolean
) => {
  const measuredLayer = layer as EdgeMeasuredLayer;
  removeEdgeMeasureLabels(measuredLayer);

  const latLngs = getPrimaryLatLngs(layer);
  if (latLngs.length < 2) return;

  const edgeCount = closeShape ? latLngs.length : latLngs.length - 1;
  measuredLayer._edgeMeasureLabels = Array.from({ length: edgeCount }, (_, index) => {
    const start = latLngs[index];
    const end = latLngs[(index + 1) % latLngs.length];
    const label = getSegmentDistance(start, end);

    return L.marker(getMidpoint(start, end), {
      interactive: false,
      keyboard: false,
      icon: L.divIcon({
        className: EDGE_LABEL_ICON_CLASS,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
        html: `<span class="${EDGE_LABEL_SPAN_CLASS}">${label}</span>`,
      }),
    }).addTo(map);
  });
};

export const installEdgeMeasureLabels = (
  layer: L.Polyline,
  map: L.Map,
  closeShape: boolean
) => {
  const measuredLayer = layer as EdgeMeasuredLayer;
  measuredLayer._edgeMeasureCleanup?.();

  const update = () => updateEdgeMeasureLabels(layer, map, closeShape);
  const cleanup = () => {
    layer.off("edit", update);
    layer.off("drag", update);
    layer.off("dragend", update);
    layer.off("move", update);
    layer.off("remove", cleanup);
    removeEdgeMeasureLabels(measuredLayer);
  };

  measuredLayer._edgeMeasureCleanup = cleanup;
  layer.on("edit", update);
  layer.on("drag", update);
  layer.on("dragend", update);
  layer.on("move", update);
  layer.on("remove", cleanup);

  update();
};
